/**
 * Pure helpers for the interactive hall-plan booth booking (user booking page and
 * admin "Hall plan & booths" tab): status vocabulary, filters, search, polygon
 * geometry, area import (JSON / SVG), checkout URL and hold countdown.
 *
 * Kept free of React, i18next and Amplify so it can be tested with node:test,
 * see scripts/booth-map/boothMap.test.js. Error strings of the import parsers are
 * plain English messages meant for display in the admin import preview.
 *
 * The status vocabulary, the booth number normalization and the hold duration
 * mirror the boothBooking Lambda (getEventBoothMap / reserveBooth) - keep them in sync.
 *
 * Booth variants (docs/interactive-booth-booking.md, "Standvarianten"): a variant product
 * (Product.boothVariant, e.g. "18B", plus boothVariantAliases) is ONE product for every hall-plan
 * area carrying that code (BoothPlanArea.variant); the exhibitor picks the booth number.
 * matchAreasToProducts, variantsOf, boothKeyOf and boothLockId exist with the same names and the
 * same algorithm in amplify/backend/function/boothBooking/src/boothLogic.js (parity-tested).
 *
 * Booth offers (docs/plan-standangebot-modell.md, option O2): areas carry features (size, positionType,
 * zone, openSides); an offer product may carry a match rule (Product.boothMatchRule) and a price rule
 * (Product/EventProduct.boothPriceRule, Product.boothPriceMode). validateBoothMatchRule,
 * validateBoothPriceRule, matchAreasToProducts (stage 3 'rule' + offers) and priceForBooth exist with the
 * same semantics in boothLogic.js and the PURE BOOTH HELPERS block of the placeOrder Lambda.
 */

export const BOOTH_PRODUCT_TYPES = ['event_booth', 'event_space'];
export const ALL_BOOTH_TYPES = [...BOOTH_PRODUCT_TYPES, 'event_booth_partner'];

export const BOOTH_STATUS = {
  FREE: 'free',
  HELD_BY_YOU: 'held_by_you',
  HELD_BY_OTHER: 'held_by_other',
  RESERVED_FOR_YOU: 'reserved_for_you',
  RESERVED_FOR_OTHER: 'reserved_for_other',
  SOLD: 'sold',
  UNAVAILABLE: 'unavailable',
};

export const HOLD_MINUTES = 30;
export const MAX_HOLDS_PER_COMPANY = 5;

const ALL_STATUSES = Object.values(BOOTH_STATUS);

// ---------------------------------------------------------------------------
// Booth numbers
// ---------------------------------------------------------------------------

/** Matching key of a booth number: trimmed, inner whitespace collapsed, uppercase ('' for empty values). */
export function normalizeBoothNumber(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Display form of a booth number: trimmed, inner whitespace collapsed, case kept. */
export function cleanBoothNumber(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim().replace(/\s+/g, ' ');
}

/** Booth numbers longer than this (trimmed) get the hint 'id-too-long' (never an error). */
export const BOOTH_NUMBER_MAX_LENGTH = 32;

/**
 * Lenient booth number check (no fixed pattern: the numbers come from the hall-plan SVG and every
 * scheme works, e.g. 'D3.5', 'A-12', '1.234', 'H3/B12', 'Z3'):
 *  - error 'missing-id': empty after trimming;
 *  - error 'reserved-id': starts with '#' (reserved for the placeholder areas of unnamed shapes);
 *  - warnings (hints only, the number stays valid): 'id-whitespace' when the trimmed value contains
 *    whitespace (normalizeBoothNumber collapses it for matching), 'id-too-long' when the trimmed value is
 *    longer than BOOTH_NUMBER_MAX_LENGTH characters.
 *
 * @returns {{ok: boolean, error: null|'missing-id'|'reserved-id', warnings: string[]}}
 */
export function checkBoothNumber(value) {
  const text = value === null || value === undefined ? '' : String(value).trim();
  if (!text) return { ok: false, error: 'missing-id', warnings: [] };
  if (text.startsWith('#')) return { ok: false, error: 'reserved-id', warnings: [] };
  const warnings = [];
  if (/\s/.test(text)) warnings.push('id-whitespace');
  if (text.length > BOOTH_NUMBER_MAX_LENGTH) warnings.push('id-too-long');
  return { ok: true, error: null, warnings };
}

/** A usable booth number: non-empty after trimming and not starting with '#' (see checkBoothNumber). */
export function isPlausibleBoothNumber(value) {
  return checkBoothNumber(value).ok;
}

/** Natural sort of booth numbers ("A2" < "A10"). */
export function compareBoothNumbers(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'de', { numeric: true });
}

/** Placeholder area number of an unnamed SVG shape ('#12', see UNNAMED_AREA_PREFIX in hallplanParser.js). */
function isPlaceholderNumber(value) {
  return value !== null && value !== undefined && String(value).trim().startsWith('#');
}

// ---------------------------------------------------------------------------
// Booth variants and booth identity
// ---------------------------------------------------------------------------

/** Variant codes are matched like booth numbers: trimmed, inner whitespace collapsed, uppercase. */
export const normalizeVariant = normalizeBoothNumber;

/** A valid variant code after trimming: letter/digit first, then letters, digits, '.', '_', '-' (max 16 characters). */
export const VARIANT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,15}$/i;

/** true when the (trimmed) code matches VARIANT_CODE_PATTERN; inner whitespace / NBSP make it invalid. */
export function isValidVariantCode(value) {
  if (value === null || value === undefined) return false;
  return VARIANT_CODE_PATTERN.test(String(value).trim());
}

/**
 * The normalized codes of a variant product / matching descriptor: [boothVariant, ...boothVariantAliases],
 * unique and non-empty; [] when boothVariant is empty (aliases alone never make a variant).
 * Identical to boothLogic.variantCodesOf / placeOrder.variantCodesOf.
 */
export function variantCodesOf(item) {
  if (!item || normalizeVariant(item.boothVariant) === '') return [];
  const codes = [];
  const add = (value) => {
    const code = normalizeVariant(value);
    if (code && !codes.includes(code)) codes.push(code);
  };
  add(item.boothVariant);
  if (Array.isArray(item.boothVariantAliases)) item.boothVariantAliases.forEach(add);
  return codes;
}

/**
 * Variant product: a booth Product (type event_booth | event_space) with a boothVariant and WITHOUT a
 * boothNumber (rule "number XOR variant"; a product carrying both counts as a single-booth product).
 */
export function isVariantProduct(product) {
  return (
    !!product &&
    BOOTH_PRODUCT_TYPES.includes(product.type) &&
    normalizeVariant(product.boothVariant) !== '' &&
    normalizeBoothNumber(product.boothNumber) === ''
  );
}

/** Hall-plan booth product: type event_booth | event_space with a boothNumber (single booth) OR a boothVariant. */
export function isBoothProduct(product) {
  return (
    !!product &&
    BOOTH_PRODUCT_TYPES.includes(product.type) &&
    (normalizeBoothNumber(product.boothNumber) !== '' || normalizeVariant(product.boothVariant) !== '')
  );
}

/**
 * Identity of a physical booth on the map: eventProductId + '#' + normalized booth number
 * (two booths of one variant product share the eventProductId). '' for a missing booth.
 */
export function boothKeyOf(booth) {
  if (!booth) return '';
  return `${booth.eventProductId ?? ''}#${normalizeBoothNumber(booth.boothNumber)}`;
}

/** StockReservation id of the single lock row of a booth in an event (hold, admin option or order claim). */
export function boothLockId(eventId, boothNumber) {
  return `boothlock-${eventId}#${normalizeBoothNumber(boothNumber)}`;
}

/** Warning codes of matchAreasToProducts (identical to boothLogic.MATCH_WARNINGS / placeOrder.MATCH_WARNINGS). */
export const MATCH_WARNINGS = Object.freeze({
  NUMBER_OVERRIDES_VARIANT: 'number-overrides-variant',
  DUPLICATE_VARIANT_PRODUCT: 'duplicate-variant-product',
  VARIANT_WITHOUT_PRODUCT: 'variant-without-product',
  NUMBER_WITHOUT_PRODUCT: 'number-without-product',
  AMBIGUOUS_OFFER_MATCH: 'ambiguous-offer-match',
  AREA_WITHOUT_OFFER: 'area-without-offer',
});

// ---------------------------------------------------------------------------
// Booth features (BoothPlanArea.positionType / zone / size / openSides)
// ---------------------------------------------------------------------------

/**
 * Standard vocabulary of BoothPlanArea.positionType (1-4 open sides). Free keys from the plan are valid
 * as well and are shown as the plan delivers them (no warning).
 */
export const POSITION_TYPES = Object.freeze(['row', 'corner', 'head', 'island']);

/** Position type derived from data-open-sides (1 row, 2 corner, 3 head, 4 island); null for anything else. */
export function positionTypeForOpenSides(openSides) {
  const n = Number(openSides);
  return Number.isInteger(n) && n >= 1 && n <= 4 ? POSITION_TYPES[n - 1] : null;
}

/** A feature key (positionType, zone, rule key) as trimmed string ('' for anything but a string); comparisons are case-insensitive on it. */
function featureText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** A numeric feature (size, openSides): a finite number or a non-blank numeric string, else null. */
function featureNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** A strict number (type number, finite) or null - prices and rule numbers. */
function strictNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Comparison form of a feature value: trimmed, lower-cased. */
function featureKey(value) {
  return featureText(value).toLowerCase();
}

/** true when two feature values are equal (trimmed, case-insensitive). */
export function sameFeature(a, b) {
  return featureKey(a) === featureKey(b);
}

/**
 * Feature combination key of an area: '<size>|<positionType>|<zone>' (trimmed, case kept); a missing size (finite
 * number or numeric string) / positionType / zone is ''. E.g. '12|corner|V', '9|row|', '||'. Feature combinations
 * are compared case-insensitively (featureKeyOf(area).toLowerCase()); the ref of ambiguous-offer-match is the key
 * of the first area seen. Identical to boothLogic.featureKeyOf / placeOrder.featureKeyOf.
 */
export function featureKeyOf(area) {
  const size = featureNumber(area && area.size);
  return `${size === null ? '' : size}|${featureText(area && area.positionType)}|${featureText(area && area.zone)}`;
}

/** true when an area carries any feature: a positionType, a zone, a size or an integer openSides. */
function hasFeatures(area) {
  if (!area) return false;
  const openSides = featureNumber(area.openSides);
  return (
    featureText(area.positionType) !== '' ||
    featureText(area.zone) !== '' ||
    featureNumber(area.size) !== null ||
    (openSides !== null && Number.isInteger(openSides))
  );
}

function positionRank(value) {
  const index = POSITION_TYPES.indexOf(featureKey(value));
  return index === -1 ? POSITION_TYPES.length : index;
}

/** null / '' last, else plain string order of the comparison form. */
function compareFeatureText(a, b) {
  const ka = featureKey(a);
  const kb = featureKey(b);
  if (ka === kb) return 0;
  if (!ka) return 1;
  if (!kb) return -1;
  return ka < kb ? -1 : 1;
}

/**
 * Named areas (placeholders '#n' and empty numbers skipped) grouped by their feature combination
 * (featureKeyOf, compared case-insensitively): [{ key: featureKeyOf of the first area, size: number | null,
 * positionType: string | null, zone: string | null (first spelling seen, trimmed), count, boothNumbers: naturally
 * sorted display numbers }].
 * Areas without any feature form the group '||'. Sorted by size ascending (none last), positionType
 * (POSITION_TYPES order, then free keys alphabetically, none last), zone (alphabetically, none last).
 */
export function groupAreasByFeatures(areas) {
  const groups = new Map();
  for (const area of Array.isArray(areas) ? areas : []) {
    if (!area || isPlaceholderNumber(area.boothNumber)) continue;
    const number = cleanBoothNumber(area.boothNumber);
    if (!number) continue;
    const key = featureKeyOf(area);
    let group = groups.get(key.toLowerCase());
    if (!group) {
      group = {
        key,
        size: featureNumber(area.size),
        positionType: featureText(area.positionType) || null,
        zone: featureText(area.zone) || null,
        count: 0,
        boothNumbers: [],
      };
      groups.set(key.toLowerCase(), group);
    }
    group.count += 1;
    group.boothNumbers.push(number);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, boothNumbers: g.boothNumbers.sort(compareBoothNumbers) }))
    .sort((a, b) => {
      if (a.size !== b.size) {
        if (a.size === null) return 1;
        if (b.size === null) return -1;
        return a.size - b.size;
      }
      const ra = positionRank(a.positionType);
      const rb = positionRank(b.positionType);
      if (ra !== rb) return ra - rb;
      return compareFeatureText(a.positionType, b.positionType) || compareFeatureText(a.zone, b.zone);
    });
}

// ---------------------------------------------------------------------------
// Offer rules (Product.boothMatchRule, Product/EventProduct.boothPriceRule)
// ---------------------------------------------------------------------------

/** Rule input (object or JSON string, AWSJSON) -> { value: object | null, error: string | null }. */
function readRuleInput(rule) {
  let value = rule;
  if (typeof value === 'string') {
    if (!value.trim()) return { value: null, error: null };
    try {
      value = JSON.parse(value);
    } catch (error) {
      return { value: null, error: 'rule is not valid JSON' };
    }
  }
  if (value === null || value === undefined) return { value: null, error: null };
  if (typeof value !== 'object' || Array.isArray(value)) return { value: null, error: 'rule must be an object' };
  return { value, error: null };
}

/**
 * Optional number field of a rule: null when absent (null / undefined); otherwise it must be a finite number
 * (type number, numeric strings are invalid) of at least `min` / greater than `above` when given.
 */
function ruleNumber(obj, key, errors, { min = null, above = null } = {}) {
  const raw = obj[key];
  if (raw === null || raw === undefined) return null;
  const n = strictNumber(raw);
  if (n === null) {
    errors.push(`${key} must be a number`);
    return null;
  }
  if (min !== null && n < min) {
    errors.push(`${key} must be at least ${min}`);
    return null;
  }
  if (above !== null && n <= above) {
    errors.push(`${key} must be greater than ${above}`);
    return null;
  }
  return n;
}

/** Optional list of feature keys: [] when absent; strings trimmed, empty ones dropped, duplicates (case-insensitive) removed. */
function ruleKeyList(obj, key, errors) {
  const raw = obj[key];
  if (raw === null || raw === undefined) return [];
  if (!Array.isArray(raw)) {
    errors.push(`${key} must be a list`);
    return [];
  }
  const out = [];
  const seen = new Set();
  for (const entry of raw) {
    if (typeof entry !== 'string') {
      errors.push(`${key} must only contain texts`);
      return [];
    }
    const text = entry.trim();
    if (!text || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
  }
  return out;
}

/**
 * Validates and normalizes a match rule (Product.boothMatchRule, object or JSON string):
 * { sizeMin?, sizeMax?, positionTypes?: string[], zones?: string[], priority? }.
 * Normalized rule: { sizeMin: number|null, sizeMax: number|null, positionTypes: string[], zones: string[],
 * priority: number (default 0) } - list entries trimmed, empty ones and case-insensitive duplicates dropped,
 * spelling kept (comparison is case-insensitive). Unknown keys are ignored.
 * No rule (null, undefined, '', 'null') -> { ok: true, rule: null, errors: [] } (only explicit codes).
 * Invalid (not an object / not JSON, sizes or priority not a finite number - numeric strings included -,
 * sizeMin > sizeMax, non-list or non-text keys) -> { ok: false, rule: null, errors } - such a rule never matches.
 * Never throws.
 *
 * @returns {{ok: boolean, rule: object|null, errors: string[]}}
 */
export function validateBoothMatchRule(rule) {
  const input = readRuleInput(rule);
  if (input.error) return { ok: false, rule: null, errors: [input.error] };
  if (!input.value) return { ok: true, rule: null, errors: [] };
  const obj = input.value;
  const errors = [];
  const sizeMin = ruleNumber(obj, 'sizeMin', errors);
  const sizeMax = ruleNumber(obj, 'sizeMax', errors);
  const positionTypes = ruleKeyList(obj, 'positionTypes', errors);
  const zones = ruleKeyList(obj, 'zones', errors);
  const priority = ruleNumber(obj, 'priority', errors);
  if (sizeMin !== null && sizeMax !== null && sizeMin > sizeMax) errors.push('sizeMin must not be greater than sizeMax');
  if (errors.length > 0) return { ok: false, rule: null, errors };
  return { ok: true, rule: { sizeMin, sizeMax, positionTypes, zones, priority: priority ?? 0 }, errors: [] };
}

/**
 * Specificity of a NORMALIZED match rule for an area: -1 when the rule does not match, else the number
 * of satisfied conditions among sizeMin, sizeMax, positionTypes (non-empty), zones (non-empty).
 * Conditions: sizeMin <= area.size <= sizeMax (a size condition never matches an area without a size); area.positionType in positionTypes; area.zone in zones (case-insensitive, trimmed; a non-empty list
 * never matches an area without that feature; an empty list is no condition).
 */
function ruleSpecificity(rule, area) {
  let specificity = 0;
  if (rule.sizeMin !== null || rule.sizeMax !== null) {
    const size = featureNumber(area && area.size);
    if (size === null) return -1;
    if (rule.sizeMin !== null) {
      if (size < rule.sizeMin) return -1;
      specificity += 1;
    }
    if (rule.sizeMax !== null) {
      if (size > rule.sizeMax) return -1;
      specificity += 1;
    }
  }
  for (const [list, value] of [[rule.positionTypes, area && area.positionType], [rule.zones, area && area.zone]]) {
    if (list.length === 0) continue;
    const key = featureKey(value);
    if (!key || !list.some((entry) => entry.toLowerCase() === key)) return -1;
    specificity += 1;
  }
  return specificity;
}

/**
 * Evaluates a match rule (raw object / JSON string, validated with validateBoothMatchRule) for an area:
 * { priority, specificity } when it matches, null when it does not or the rule is missing / invalid.
 */
export function boothMatchRuleResult(rule, area) {
  const { rule: normalized } = validateBoothMatchRule(rule);
  if (!normalized) return null;
  const specificity = ruleSpecificity(normalized, area);
  return specificity < 0 ? null : { priority: normalized.priority, specificity };
}

export const PRICE_SURCHARGE_ON = Object.freeze(['positionType', 'zone']);
export const PRICE_SURCHARGE_TYPES = Object.freeze(['percent', 'absolute', 'rate']);
export const ROUND_SQM_MODES = Object.freeze(['none', 'ceil', 'floor', 'round']);

/**
 * Validates and normalizes a price rule (Product.boothPriceRule / EventProduct.boothPriceRule, object or
 * JSON string): { surcharges?: [{ on: 'positionType'|'zone', key, type: 'percent'|'absolute'|'rate', value,
 * capSqm? }], minSqm?, minPrice?, roundSqm?: 'none'|'ceil'|'floor'|'round' }.
 * Normalized rule: { surcharges: [{ on, key (trimmed), type, value: number, capSqm: number|null }],
 * minSqm: number|null, minPrice: number|null, roundSqm (default 'none') }. Unknown keys are ignored.
 * No rule (null, undefined, '', 'null') -> { ok: true, rule: null, errors: [] }.
 * Invalid (not an object / not JSON, surcharges not a list, unknown on/type (trimmed), empty or non-text key,
 * value not a finite number, capSqm not a number > 0, minSqm / minPrice not a number >= 0 - numeric strings
 * are invalid -, unknown roundSqm) -> { ok: false, rule: null, errors }.
 * Never throws.
 *
 * @returns {{ok: boolean, rule: object|null, errors: string[]}}
 */
export function validateBoothPriceRule(rule) {
  const input = readRuleInput(rule);
  if (input.error) return { ok: false, rule: null, errors: [input.error] };
  if (!input.value) return { ok: true, rule: null, errors: [] };
  const obj = input.value;
  const errors = [];
  const surcharges = [];
  const rawSurcharges = obj.surcharges;
  if (rawSurcharges !== null && rawSurcharges !== undefined && !Array.isArray(rawSurcharges)) {
    errors.push('surcharges must be a list');
  } else {
    (rawSurcharges || []).forEach((entry, index) => {
      const label = `surcharges[${index}]`;
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        errors.push(`${label} must be an object`);
        return;
      }
      const entryErrors = [];
      const on = typeof entry.on === 'string' ? entry.on.trim() : entry.on;
      const type = typeof entry.type === 'string' ? entry.type.trim() : entry.type;
      if (!PRICE_SURCHARGE_ON.includes(on)) entryErrors.push(`${label}.on must be one of ${PRICE_SURCHARGE_ON.join(', ')}`);
      const key = featureText(entry.key);
      if (!key) entryErrors.push(`${label}.key must be a non-empty text`);
      if (!PRICE_SURCHARGE_TYPES.includes(type)) entryErrors.push(`${label}.type must be one of ${PRICE_SURCHARGE_TYPES.join(', ')}`);
      const numbers = [];
      const value = ruleNumber(entry, 'value', numbers);
      const capSqm = ruleNumber(entry, 'capSqm', numbers, { above: 0 });
      if (entry.value === null || entry.value === undefined) numbers.push('value is required');
      numbers.forEach((message) => entryErrors.push(`${label}.${message}`));
      if (entryErrors.length > 0) {
        errors.push(...entryErrors);
        return;
      }
      surcharges.push({ on, key, type, value, capSqm });
    });
  }
  const minSqm = ruleNumber(obj, 'minSqm', errors, { min: 0 });
  const minPrice = ruleNumber(obj, 'minPrice', errors, { min: 0 });
  let roundSqm = 'none';
  if (obj.roundSqm !== null && obj.roundSqm !== undefined) {
    if (ROUND_SQM_MODES.includes(obj.roundSqm)) roundSqm = obj.roundSqm;
    else errors.push(`roundSqm must be one of ${ROUND_SQM_MODES.join(', ')}`);
  }
  if (errors.length > 0) return { ok: false, rule: null, errors };
  return { ok: true, rule: { surcharges, minSqm, minPrice, roundSqm }, errors: [] };
}

function roundSqmValue(sqm, mode) {
  if (mode === 'ceil') return Math.ceil(sqm);
  if (mode === 'floor') return Math.floor(sqm);
  if (mode === 'round') return Math.round(sqm);
  return sqm;
}

/** Timestamp (ms) of an ISO string / Date / number, NaN when not a valid point in time. */
function timeOf(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim()) return Date.parse(value);
  return NaN;
}

/**
 * The first entry of EventProduct.timeRangePrices (array order) whose range contains `now` (ms):
 * start <= now <= end, both bounds inclusive, a missing bound is open, at least one bound required; the
 * entry needs a finite price. -> { start, end, price } (start / end as stored, null when missing) or null.
 */
function timeTierFor(timeRangePrices, now) {
  if (!Array.isArray(timeRangePrices) || !Number.isFinite(now)) return null;
  for (const entry of timeRangePrices) {
    const range = entry && entry.timeRange;
    const price = strictNumber(entry && entry.price);
    if (!range || price === null) continue;
    const hasStart = !(range.start === null || range.start === undefined || (typeof range.start === 'string' && !range.start.trim()));
    const hasEnd = !(range.end === null || range.end === undefined || (typeof range.end === 'string' && !range.end.trim()));
    if (!hasStart && !hasEnd) continue;
    const start = hasStart ? timeOf(range.start) : -Infinity;
    const end = hasEnd ? timeOf(range.end) : Infinity;
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    if (now >= start && now <= end) return { start: hasStart ? range.start : null, end: hasEnd ? range.end : null, price };
  }
  return null;
}

/**
 * Price of one booth (offer product on an area) at `nowIso` (time of the order; ISO string, Date or ms).
 *  - price: EventProduct.price when not null, else Product.price (finite numbers only); a time tier of
 *    EventProduct.timeRangePrices containing nowIso (first match in array order, see timeTierFor) replaces it;
 *    no finite price -> null;
 *  - mode: product.boothPriceMode === 'perSqm' ? 'perSqm' : 'fixed';
 *  - priceRule: validateBoothPriceRule(eventProduct.boothPriceRule ?? product.boothPriceRule).rule (invalid -> none);
 *  - sqm: area.size, else product.boothArea (finite number or numeric string), else null; rounded by priceRule.roundSqm
 *    (default none);
 *  - base: fixed = price; perSqm = price * sqm (no sqm -> the result is null);
 *  - surcharges whose key equals the area's positionType / zone (trimmed, case-insensitive), in rule order:
 *    'rate' (perSqm only, ignored for fixed): amount = (value - price) * sqm;
 *    'percent': perSqm with capSqm: price * min(sqm, capSqm) * value / 100, else base * value / 100;
 *    'absolute': amount = value;
 *  - total = base + sum(amounts); with minPrice set and total < minPrice, total = minPrice and
 *    minPriceApplied = true; total rounded to 2 decimals (Math.round(total * 100) / 100), base and amounts
 *    unrounded. minSqm is no price condition (ignored).
 * Without mode and rules (LOUNGES) total === EventProduct.price (rounded to cents).
 *
 * @returns {{total: number, base: number, sqm: number|null, mode: 'fixed'|'perSqm', tier: {start, end, price}|null,
 *   surcharges: Array<{on, key, type, value, amount}>, minPriceApplied: boolean}|null}
 */
export function priceForBooth({ product = null, eventProduct = null, area = null, nowIso = null } = {}) {
  const tier = timeTierFor(eventProduct && eventProduct.timeRangePrices, timeOf(nowIso));
  const listPrice = eventProduct && eventProduct.price !== null && eventProduct.price !== undefined
    ? strictNumber(eventProduct.price)
    : strictNumber(product && product.price);
  const price = tier ? tier.price : listPrice;
  if (price === null) return null;
  const mode = product && product.boothPriceMode === 'perSqm' ? 'perSqm' : 'fixed';
  const ruleSource = eventProduct && eventProduct.boothPriceRule !== null && eventProduct.boothPriceRule !== undefined
    ? eventProduct.boothPriceRule
    : product && product.boothPriceRule;
  const rule = validateBoothPriceRule(ruleSource).rule;
  const rawSqm = featureNumber(area && area.size) ?? featureNumber(product && product.boothArea);
  const sqm = rawSqm === null ? null : roundSqmValue(rawSqm, rule ? rule.roundSqm : 'none');
  if (mode === 'perSqm' && sqm === null) return null;
  const base = mode === 'perSqm' ? price * sqm : price;
  const surcharges = [];
  for (const s of rule ? rule.surcharges : []) {
    const feature = s.on === 'zone' ? area && area.zone : area && area.positionType;
    if (!featureText(feature) || !sameFeature(feature, s.key)) continue;
    let amount;
    if (s.type === 'rate') {
      if (mode !== 'perSqm') continue;
      amount = (s.value - price) * sqm;
    } else if (s.type === 'percent') {
      amount = mode === 'perSqm' && s.capSqm !== null ? (price * Math.min(sqm, s.capSqm) * s.value) / 100 : (base * s.value) / 100;
    } else {
      amount = s.value;
    }
    surcharges.push({ on: s.on, key: s.key, type: s.type, value: s.value, amount });
  }
  let total = base + surcharges.reduce((sum, s) => sum + s.amount, 0);
  let minPriceApplied = false;
  if (rule && rule.minPrice !== null && total < rule.minPrice) {
    total = rule.minPrice;
    minPriceApplied = true;
  }
  return { total: Math.round(total * 100) / 100, base, sqm, mode, tier, surcharges, minPriceApplied };
}

function sortKeyString(value) {
  return value === undefined || value === null ? '' : String(value);
}

/**
 * Booth products in matching priority: sortKey (= EventProduct.createdAt, an ISO string) ascending with an
 * empty sortKey FIRST, then eventProductId ascending - plain string order, locale independent.
 * Identical to boothLogic.compareBoothProducts / placeOrder.compareBoothProducts.
 */
export function compareBoothProducts(a, b) {
  const ka = sortKeyString(a.sortKey);
  const kb = sortKeyString(b.sortKey);
  if (ka !== kb) return ka < kb ? -1 : 1;
  const ea = sortKeyString(a.eventProductId);
  const eb = sortKeyString(b.eventProductId);
  if (ea !== eb) return ea < eb ? -1 : 1;
  return 0;
}

/**
 * Variant codes of an event's booth products: Map<normalized code, boothProduct[]> where a code is a
 * product's boothVariant or one of its boothVariantAliases. Only variant products (boothVariant set,
 * boothNumber empty) take part. The list of each code is in matching priority (compareBoothProducts):
 * the first product wins, more than one entry = duplicate variant product. Codes are in order of their
 * first appearance in that priority order. Identical to boothLogic.variantsOf (parity-tested in
 * scripts/booth-map/matchParity.test.js).
 *
 * @param {Array<{eventProductId, productId, boothNumber?, boothVariant?, boothVariantAliases?, sortKey?}>} boothProducts
 */
export function variantsOf(boothProducts) {
  const byCode = new Map();
  const sorted = (Array.isArray(boothProducts) ? boothProducts : [])
    .filter((item) => item && normalizeBoothNumber(item.boothNumber) === '' && normalizeVariant(item.boothVariant) !== '')
    .slice()
    .sort(compareBoothProducts);
  for (const item of sorted) {
    for (const code of variantCodesOf(item)) {
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code).push(item);
    }
  }
  return byCode;
}

/**
 * THE matching of live hall-plan areas to the event's booth products (design 3.2). Same algorithm, warning
 * order and refs as amplify/backend/function/boothBooking/src/boothLogic.js and the placeOrder Lambda - keep
 * in sync; parity-tested in scripts/booth-map/matchParity.test.js.
 * areas: live BoothPlanArea rows (placeholders '#n', empty and repeated normalized numbers are skipped, the
 * first area per number counts); boothProducts: descriptors
 * { eventProductId, productId, boothNumber, boothVariant, boothVariantAliases, sortKey (= EventProduct.createdAt) }.
 * Per area (areaKey = normalized booth number):
 *   1. a single-booth product with that number -> matched (via 'number'); when the area also has a variant
 *      code with a variant product -> warning number-overrides-variant { ref: areaKey, variant }
 *   2. else the area's variant code equals a variant product's boothVariant or alias -> matched (via
 *      'variant'); when several variant products share a code the first by compareBoothProducts wins
 *   3. else rule: every product WITHOUT boothNumber whose boothMatchRule (validateBoothMatchRule; null or
 *      invalid = no rule) matches the area's features (see boothMatchRuleResult) -> matched (via 'rule') to
 *      the first by priority desc, specificity desc, compareBoothProducts; when two or more rule matches tie
 *      with the first on priority and specificity -> ambiguous-offer-match { ref: featureKeyOf(area),
 *      eventProductIds: the tied ones in that order } once per feature combination (compared case-insensitively,
 *      ref = key of the first area seen)
 *   4. else unassigned: variant-without-product { ref: code, count } once per code (pushed when the code
 *      is first seen, the count grows afterwards) for areas with a code; area-without-offer { ref: areaKey }
 *      per area without a code but with features (positionType, zone, positive size or openSides);
 *      number-without-product { ref: areaKey } per named area with neither code nor features
 * duplicate-variant-product { ref: code, eventProductIds } is emitted once per code shared by two or more
 * variant products of the event (independent of the areas), before the per-area warnings.
 * offers: per matched area the ordered offer descriptors (first = primary = matches entry): stages 1 and 2
 * exactly the matched product, stage 3 every matching rule product in rule order.
 *
 * @param {Array<{boothNumber: string, variant?: string|null, size?, positionType?, zone?, openSides?}>} areas
 * @param {Array<{eventProductId, productId, boothNumber?, boothVariant?, boothVariantAliases?, boothMatchRule?, sortKey?}>} boothProducts
 * @returns {{matches: Map<string, {eventProductId, productId, via: 'number'|'variant'|'rule'}>, byProduct: Map<string, string[]>,
 *   offers: Map<string, object[]>, warnings: object[]}}
 *   matches/byProduct/offers keyed by areaKey = normalized booth number; byProduct only lists primary offers.
 */
export function matchAreasToProducts(areas, boothProducts) {
  const warnings = [];
  const sorted = (Array.isArray(boothProducts) ? boothProducts : []).filter(Boolean).slice().sort(compareBoothProducts);
  const byNumber = new Map();
  const byCode = new Map();
  const duplicates = new Map();
  const ruleProducts = [];
  for (const item of sorted) {
    const number = normalizeBoothNumber(item.boothNumber);
    if (number) {
      if (!byNumber.has(number)) byNumber.set(number, item);
      continue;
    }
    const { rule } = validateBoothMatchRule(item.boothMatchRule);
    if (rule) ruleProducts.push({ item, rule });
    for (const code of variantCodesOf(item)) {
      const holder = byCode.get(code);
      if (!holder) {
        byCode.set(code, item);
      } else if (holder.eventProductId !== item.eventProductId) {
        const warning = duplicates.get(code);
        if (warning) {
          if (!warning.eventProductIds.includes(item.eventProductId)) warning.eventProductIds.push(item.eventProductId);
        } else {
          const created = { code: MATCH_WARNINGS.DUPLICATE_VARIANT_PRODUCT, ref: code, eventProductIds: [holder.eventProductId, item.eventProductId] };
          duplicates.set(code, created);
          warnings.push(created);
        }
      }
    }
  }

  const matches = new Map();
  const byProduct = new Map();
  const offers = new Map();
  const missingVariants = new Map();
  const ambiguous = new Set();
  const seen = new Set();
  const assign = (key, list, via) => {
    const item = list[0];
    matches.set(key, { eventProductId: item.eventProductId, productId: item.productId, via });
    offers.set(key, list);
    if (!byProduct.has(item.eventProductId)) byProduct.set(item.eventProductId, []);
    byProduct.get(item.eventProductId).push(key);
  };
  for (const area of Array.isArray(areas) ? areas : []) {
    if (!area || isPlaceholderNumber(area.boothNumber)) continue;
    const key = normalizeBoothNumber(area.boothNumber);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const code = normalizeVariant(area.variant);
    const single = byNumber.get(key);
    const variantProduct = code ? byCode.get(code) : null;
    if (single) {
      assign(key, [single], 'number');
      if (variantProduct) warnings.push({ code: MATCH_WARNINGS.NUMBER_OVERRIDES_VARIANT, ref: key, variant: code });
      continue;
    }
    if (variantProduct) {
      assign(key, [variantProduct], 'variant');
      continue;
    }
    const ranked = rankRuleProducts(ruleProducts, area);
    if (ranked.length > 0) {
      assign(key, ranked.map((r) => r.item), 'rule');
      const tied = ranked.filter((r) => r.priority === ranked[0].priority && r.specificity === ranked[0].specificity);
      const featureRef = featureKeyOf(area);
      if (tied.length > 1 && !ambiguous.has(featureRef.toLowerCase())) {
        ambiguous.add(featureRef.toLowerCase());
        warnings.push({ code: MATCH_WARNINGS.AMBIGUOUS_OFFER_MATCH, ref: featureRef, eventProductIds: tied.map((r) => r.item.eventProductId) });
      }
    } else if (code) {
      const warning = missingVariants.get(code);
      if (warning) {
        warning.count += 1;
      } else {
        const created = { code: MATCH_WARNINGS.VARIANT_WITHOUT_PRODUCT, ref: code, count: 1 };
        missingVariants.set(code, created);
        warnings.push(created);
      }
    } else if (hasFeatures(area)) {
      warnings.push({ code: MATCH_WARNINGS.AREA_WITHOUT_OFFER, ref: key });
    } else {
      warnings.push({ code: MATCH_WARNINGS.NUMBER_WITHOUT_PRODUCT, ref: key });
    }
  }
  return { matches, byProduct, offers, warnings };
}

/**
 * Rule products ([{ item, rule: normalized }], in compareBoothProducts order) matching an area, ranked:
 * priority desc, specificity desc, then compareBoothProducts. -> [{ item, priority, specificity }].
 */
function rankRuleProducts(ruleProducts, area) {
  const ranked = [];
  for (const { item, rule } of ruleProducts) {
    const specificity = ruleSpecificity(rule, area);
    if (specificity >= 0) ranked.push({ item, priority: rule.priority, specificity });
  }
  return ranked.sort((a, b) => b.priority - a.priority || b.specificity - a.specificity || compareBoothProducts(a.item, b.item));
}

/**
 * The offers of an area by rule only (stage 3 of matchAreasToProducts): descriptors WITHOUT boothNumber
 * whose boothMatchRule matches, ranked (priority desc, specificity desc, compareBoothProducts). [] when none.
 */
export function ruleOffersFor(area, boothProducts) {
  const ruleProducts = [];
  const sorted = (Array.isArray(boothProducts) ? boothProducts : []).filter(Boolean).slice().sort(compareBoothProducts);
  for (const item of sorted) {
    if (normalizeBoothNumber(item.boothNumber)) continue;
    const { rule } = validateBoothMatchRule(item.boothMatchRule);
    if (rule) ruleProducts.push({ item, rule });
  }
  return rankRuleProducts(ruleProducts, area || {}).map((r) => r.item);
}

function mostCommonSize(sizes) {
  const counts = new Map();
  for (const size of sizes) counts.set(size, (counts.get(size) || 0) + 1);
  let best = null;
  let bestCount = 0;
  for (const [size, count] of counts) {
    if (count > bestCount || (count === bestCount && size < best)) {
      best = size;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Areas grouped by their variant code (areas without a variant and placeholders '#n' are skipped):
 * [{ variant: normalized code, count, size: most common positive size (m²) or null, sizes: distinct
 * sizes ascending, boothNumbers: naturally sorted, areas }], sorted by code (natural order).
 */
export function groupAreasByVariant(areas) {
  const groups = new Map();
  for (const area of Array.isArray(areas) ? areas : []) {
    if (!area || isPlaceholderNumber(area.boothNumber) || !cleanBoothNumber(area.boothNumber)) continue;
    const code = normalizeVariant(area.variant);
    if (!code) continue;
    if (!groups.has(code)) groups.set(code, []);
    groups.get(code).push(area);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => compareBoothNumbers(a, b))
    .map(([variant, list]) => {
      const sizes = list.map((a) => positiveNumberOrNull(a.size)).filter((s) => s !== null);
      return {
        variant,
        count: list.length,
        size: mostCommonSize(sizes),
        sizes: [...new Set(sizes)].sort((a, b) => a - b),
        boothNumbers: list.map((a) => cleanBoothNumber(a.boothNumber)).sort(compareBoothNumbers),
        areas: list,
      };
    });
}

/**
 * Parses the "Assign variants from list" text (Excel layout, copied rows): one variant per line,
 * `code<TAB or ;>[count<TAB or ;>]N1, N2, ...`. The count column is optional and only reported
 * (never checked against the list). A first line without any digit in its number column is taken
 * as a header and skipped. Codes and numbers are normalized; a number listed for two different
 * codes keeps the first and is reported. Error strings are plain English for the admin preview.
 *
 * @returns {{assignments: Map<string, string>, codes: Array<{code: string, count: number|null, numbers: string[]}>, errors: string[]}}
 *   assignments: normalized booth number -> normalized code; codes in order of first appearance,
 *   numbers as listed (trimmed, duplicates removed).
 */
export function parseVariantAssignmentList(text) {
  const assignments = new Map();
  const codes = [];
  const errors = [];
  const byCode = new Map();
  const lines = typeof text === 'string' ? text.split(/\r\n|\r|\n/) : [];
  let contentLines = 0;
  lines.forEach((rawLine, index) => {
    if (!rawLine.trim()) return;
    contentLines += 1;
    const label = `Line ${index + 1}`;
    // split the untrimmed line: a trailing empty cell (TAB at the end) still counts as a column
    const columns = rawLine.split(/[\t;]/).map((c) => c.trim());
    // 3+ columns = code, count, numbers (an empty trailing number cell stays "no booth numbers")
    if (columns.length < 2) {
      errors.push(`${label}: expected "code<TAB or ;>booth numbers"`);
      return;
    }
    const rawCode = columns[0];
    const numbersText = columns.length >= 3 ? columns.slice(2).join(',') : columns[1];
    const countText = columns.length >= 3 ? columns[1] : '';
    const numbers = numbersText
      .split(',')
      .map((n) => cleanBoothNumber(n))
      .filter(Boolean);
    if (contentLines === 1 && !/\d/.test(numbersText)) return; // header row ("Variante  Anzahl  Stände")
    if (!isValidVariantCode(rawCode)) {
      errors.push(`${label}: "${rawCode}" is not a valid variant code`);
      return;
    }
    if (numbers.length === 0) {
      errors.push(`${label}: no booth numbers for "${rawCode}"`);
      return;
    }
    const code = normalizeVariant(rawCode);
    const countValue = countText === '' ? null : Number(countText.replace(',', '.'));
    let entry = byCode.get(code);
    if (!entry) {
      entry = { code, count: Number.isFinite(countValue) ? countValue : null, numbers: [] };
      byCode.set(code, entry);
      codes.push(entry);
    } else if (Number.isFinite(countValue)) {
      entry.count = (entry.count || 0) + countValue;
    }
    for (const number of numbers) {
      const key = normalizeBoothNumber(number);
      const assigned = assignments.get(key);
      if (assigned === undefined) {
        assignments.set(key, code);
        entry.numbers.push(number);
      } else if (assigned !== code) {
        errors.push(`${label}: booth "${number}" is already listed for variant "${assigned}"`);
      }
    }
  });
  return { assignments, codes, errors };
}

// ---------------------------------------------------------------------------
// Sizes and statuses
// ---------------------------------------------------------------------------

/** The caller can reserve / check out this booth. */
export function isBookableStatus(status) {
  return status === BOOTH_STATUS.FREE || status === BOOTH_STATUS.HELD_BY_YOU || status === BOOTH_STATUS.RESERVED_FOR_YOU;
}

/** The caller can join / leave the waitlist of this booth. */
export function isWaitlistableStatus(status) {
  return status === BOOTH_STATUS.HELD_BY_OTHER || status === BOOTH_STATUS.RESERVED_FOR_OTHER;
}

/** Every known status can be selected on the map (sold shows the exhibitor); only bookable/waitlistable ones have actions. */
export function isSelectableStatus(status) {
  return ALL_STATUSES.includes(status);
}

// ---------------------------------------------------------------------------
// Filters and search
// ---------------------------------------------------------------------------

/**
 * Price (€) and area (m²) limits: null = open end (slider thumb at the end of its range).
 * positionTypes / zones / offerCodes: chip selections, [] = no condition.
 */
export function defaultFilters() {
  return { query: '', priceMin: null, priceMax: null, areaMin: null, areaMax: null, positionTypes: [], zones: [], offerCodes: [] };
}

/** Feature value in the selected list (trimmed, case-insensitive); an empty / missing list is no condition. */
function matchesFeatureList(value, list) {
  if (!Array.isArray(list) || list.length === 0) return true;
  const key = featureKey(value);
  return key !== '' && list.some((entry) => featureKey(entry) === key);
}

/**
 * Offer filter: the selected entries are offer codes or eventProductIds (normalized like booth numbers);
 * a booth matches when its code (booth.variant) or the eventProductId of the booth or of one of its
 * offers[] is selected. An empty / missing list is no condition.
 */
function matchesOfferCodes(booth, list) {
  if (!Array.isArray(list) || list.length === 0) return true;
  const selected = new Set(list.map(normalizeVariant).filter(Boolean));
  if (selected.size === 0) return true;
  const candidates = [booth.variant, booth.eventProductId, ...(Array.isArray(booth.offers) ? booth.offers.map((o) => o && o.eventProductId) : [])];
  return candidates.some((value) => {
    const key = normalizeVariant(value);
    return key !== '' && selected.has(key);
  });
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** value inside [min, max] (null = open end); `unknown` is the result for a missing value while a limit is set. */
function withinLimits(value, min, max, unknown) {
  const lower = numberOrNull(min);
  const upper = numberOrNull(max);
  if (lower === null && upper === null) return true;
  const number = numberOrNull(value);
  if (number === null) return unknown;
  return (lower === null || number >= lower) && (upper === null || number <= upper);
}

function matchesQuery(booth, query) {
  const raw = typeof query === 'string' ? query.trim() : '';
  if (!raw) return true;
  const needle = normalizeBoothNumber(raw);
  if (normalizeBoothNumber(booth.boothNumber).includes(needle)) return true;
  const exhibitor = booth.exhibitor && typeof booth.exhibitor.name === 'string' ? booth.exhibitor.name : '';
  return exhibitor.toLowerCase().includes(raw.toLowerCase());
}

/**
 * Whether a booth matches the filter bar. Sold and unavailable booths never match
 * (they are drawn as such, not as "filtered out").
 *
 * A booth without a price passes the price limits; a booth without an area fails active area limits.
 * Selected position types / zones need the booth's positionType / zone in the list (case-insensitive; a
 * booth without the feature fails), selected offer codes see matchesOfferCodes.
 *
 * @param {object} booth map booth (MAP CONTRACT)
 * @param {object} filters see defaultFilters()
 */
export function boothMatchesFilters(booth, filters) {
  if (!booth) return false;
  const f = filters || defaultFilters();
  if (booth.status === BOOTH_STATUS.SOLD || booth.status === BOOTH_STATUS.UNAVAILABLE) return false;
  if (!matchesQuery(booth, f.query)) return false;
  if (!withinLimits(booth.price, f.priceMin, f.priceMax, true)) return false;
  if (!withinLimits(booth.area, f.areaMin, f.areaMax, false)) return false;
  if (!matchesFeatureList(booth.positionType, f.positionTypes)) return false;
  if (!matchesFeatureList(booth.zone, f.zones)) return false;
  return matchesOfferCodes(booth, f.offerCodes);
}

/**
 * Quick search (search box suggestions): booths whose number or exhibitor name contains the
 * query (at least 2 characters), booth number prefix hits first, at most 8 results.
 */
export function searchBooths(booths, query) {
  const raw = typeof query === 'string' ? query.trim() : '';
  if (raw.length < 2 || !Array.isArray(booths)) return [];
  const needle = normalizeBoothNumber(raw);
  const lower = raw.toLowerCase();
  const prefix = [];
  const other = [];
  for (const booth of booths) {
    if (!booth) continue;
    const number = normalizeBoothNumber(booth.boothNumber);
    const exhibitor = booth.exhibitor && typeof booth.exhibitor.name === 'string' ? booth.exhibitor.name.toLowerCase() : '';
    if (number.startsWith(needle)) prefix.push(booth);
    else if (number.includes(needle) || exhibitor.includes(lower)) other.push(booth);
  }
  const byNumber = (a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber);
  return [...prefix.sort(byNumber), ...other.sort(byNumber)].slice(0, 8);
}

/** Span of a numeric booth field, min rounded down and max rounded up to `step`; { 0, 0 } without values. */
function fieldRange(booths, field, step) {
  const values = (Array.isArray(booths) ? booths : [])
    .map((b) => (b ? numberOrNull(b[field]) : null))
    .filter((v) => v !== null && v >= 0);
  if (values.length === 0) return { min: 0, max: 0 };
  return {
    min: Math.floor(Math.min(...values) / step) * step,
    max: Math.ceil(Math.max(...values) / step) * step,
  };
}

/** Price span of the booths for the price slider, min rounded down and max rounded up to 500. */
export function priceRange(booths) {
  return fieldRange(booths, 'price', 500);
}

/** Area span (m²) of the booths for the size slider, rounded to whole m². */
export function areaRange(booths) {
  return fieldRange(booths, 'area', 1);
}

// ---------------------------------------------------------------------------
// Polygon geometry ("x,y x,y ..." in BoothPlan coordinate space)
// ---------------------------------------------------------------------------

/** "x,y x,y" -> [[x, y], ...]; [] when the string is empty or not an even list of numbers. */
export function parsePoints(points) {
  if (Array.isArray(points)) {
    const pairs = points.map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : [Number(p?.x), Number(p?.y)]));
    return pairs.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y)) ? pairs : [];
  }
  if (typeof points !== 'string') return [];
  const text = points.trim();
  if (!text) return [];
  const tokens = text.split(/[\s,]+/).filter(Boolean);
  if (tokens.length % 2 !== 0) return [];
  const numbers = tokens.map((token) => (/^[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/.test(token) ? Number(token) : NaN));
  if (numbers.some((n) => !Number.isFinite(n))) return [];
  const pairs = [];
  for (let i = 0; i < numbers.length; i += 2) pairs.push([numbers[i], numbers[i + 1]]);
  return pairs;
}

function formatCoordinate(value) {
  const rounded = Math.round(Number(value) * 100) / 100;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

/** [[x, y], ...] -> "x,y x,y" (2 decimals at most). */
export function pointsToString(pairs) {
  if (!Array.isArray(pairs)) return '';
  return pairs.map(([x, y]) => `${formatCoordinate(x)},${formatCoordinate(y)}`).join(' ');
}

/** Centroid of a polygon (area-weighted; vertex average for degenerate polygons). */
export function polygonCentroid(points) {
  const pairs = parsePoints(points);
  if (pairs.length === 0) return { x: 0, y: 0 };
  let area2 = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pairs.length; i += 1) {
    const [x0, y0] = pairs[i];
    const [x1, y1] = pairs[(i + 1) % pairs.length];
    const cross = x0 * y1 - x1 * y0;
    area2 += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  if (Math.abs(area2) < 1e-9) {
    const sum = pairs.reduce((acc, [x, y]) => ({ x: acc.x + x, y: acc.y + y }), { x: 0, y: 0 });
    return { x: sum.x / pairs.length, y: sum.y / pairs.length };
  }
  return { x: cx / (3 * area2), y: cy / (3 * area2) };
}

/** Bounding box of a polygon. */
export function polygonBounds(points) {
  const pairs = parsePoints(points);
  if (pairs.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  const xs = pairs.map((p) => p[0]);
  const ys = pairs.map((p) => p[1]);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

function roundLabel(value) {
  return Math.round(value * 10) / 10;
}

// ---------------------------------------------------------------------------
// Area import
// ---------------------------------------------------------------------------

function positiveNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function finiteOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Drops a closing vertex that repeats the first one. */
function openRing(pairs) {
  if (pairs.length > 1) {
    const [fx, fy] = pairs[0];
    const [lx, ly] = pairs[pairs.length - 1];
    if (Math.abs(fx - lx) < 1e-9 && Math.abs(fy - ly) < 1e-9) return pairs.slice(0, -1);
  }
  return pairs;
}

/** Builds an import area, or returns an error string. */
function buildArea(boothNumber, pairs, extra, label) {
  const ring = openRing(pairs);
  if (ring.length < 3) return `${label}: the outline needs at least 3 points`;
  const points = pointsToString(ring);
  const centroid = polygonCentroid(points);
  const labelX = finiteOrNull(extra.labelX);
  const labelY = finiteOrNull(extra.labelY);
  return {
    boothNumber,
    points,
    labelX: labelX !== null && labelY !== null ? labelX : roundLabel(centroid.x),
    labelY: labelX !== null && labelY !== null ? labelY : roundLabel(centroid.y),
    area: positiveNumberOrNull(extra.area),
    width: positiveNumberOrNull(extra.width),
    depth: positiveNumberOrNull(extra.depth),
    source: 'import',
  };
}

/** Keeps the first area per normalized booth number, reports the others. */
function dedupeAreas(areas, errors) {
  const seen = new Set();
  const out = [];
  for (const area of areas) {
    const key = normalizeBoothNumber(area.boothNumber);
    if (seen.has(key)) {
      errors.push(`Duplicate booth number "${area.boothNumber}": only the first outline is kept`);
      continue;
    }
    seen.add(key);
    out.push(area);
  }
  return out;
}

/**
 * variant / size / bookable of a JSON import entry (written by areasToExportJson). Keys only when
 * present, so files without them parse exactly as before; an invalid variant code is reported and dropped.
 */
function importVariantFields(entry, label, errors) {
  const out = {};
  const variant = entry.variant === null || entry.variant === undefined ? '' : String(entry.variant).trim();
  if (variant) {
    if (isValidVariantCode(variant)) out.variant = variant;
    else errors.push(`${label}: invalid variant code "${variant}" was ignored`);
  }
  const size = positiveNumberOrNull(entry.size);
  if (size !== null) out.size = size;
  if (entry.bookable === false) out.bookable = false;
  Object.assign(out, featureFields(entry));
  return out;
}

/** positionType / zone (trimmed) / openSides (integer 1-4) of an area-like object; keys only when set. */
function featureFields(entry) {
  const out = {};
  const positionType = featureText(entry.positionType);
  if (positionType) out.positionType = positionType;
  const zone = featureText(entry.zone);
  if (zone) out.zone = zone;
  if (positionTypeForOpenSides(entry.openSides)) out.openSides = Number(entry.openSides);
  return out;
}

/**
 * Parses an area import file: either { width, height, areas: [...] } or a bare array of
 * { boothNumber, points ("x,y x,y" or [[x,y],...]), labelX?, labelY?, area?, width?, depth?,
 * variant?, size?, bookable?, positionType?, zone?, openSides? }.
 *
 * @returns {{width: number|null, height: number|null, areas: object[], errors: string[]}}
 */
export function parseAreasFromJson(textOrObject) {
  const errors = [];
  let data = textOrObject;
  if (typeof textOrObject === 'string') {
    try {
      data = JSON.parse(textOrObject);
    } catch (error) {
      return { width: null, height: null, areas: [], errors: [`The file is not valid JSON: ${error.message}`] };
    }
  }

  let list;
  let width = null;
  let height = null;
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === 'object' && Array.isArray(data.areas)) {
    list = data.areas;
    width = positiveNumberOrNull(data.width);
    height = positiveNumberOrNull(data.height);
  } else {
    return { width: null, height: null, areas: [], errors: ['Expected an array of areas or an object with an "areas" array'] };
  }

  const areas = [];
  list.forEach((entry, index) => {
    const position = `Area #${index + 1}`;
    if (!entry || typeof entry !== 'object') {
      errors.push(`${position}: not an object`);
      return;
    }
    const boothNumber = cleanBoothNumber(entry.boothNumber);
    if (!boothNumber) {
      errors.push(`${position}: missing booth number`);
      return;
    }
    const pairs = parsePoints(entry.points);
    if (pairs.length === 0) {
      errors.push(`${position} ("${boothNumber}"): invalid points`);
      return;
    }
    const label = `${position} ("${boothNumber}")`;
    const area = buildArea(boothNumber, pairs, entry, label);
    if (typeof area === 'string') errors.push(area);
    else areas.push({ ...area, ...importVariantFields(entry, label, errors) });
  });

  return { width, height, areas: dedupeAreas(areas, errors), errors };
}

// --- SVG ---------------------------------------------------------------------

/** Containers whose content is never drawn directly. */
const SKIPPED_CONTAINERS = new Set(['defs', 'clippath', 'mask', 'pattern', 'symbol', 'marker', 'lineargradient', 'radialgradient', 'filter', 'metadata', 'style', 'script']);
/** Generic ids written by drawing tools (Illustrator, Inkscape, PDF converters) that are no booth numbers. */
const GENERIC_ID_RE = /^(?:layer|ebene|group|gruppe|path|rect|polygon|polyline|svg|clip|clippath|font|glyph|defs|mask|image|bild|background|hintergrund|inkscape|outline|text)(?:[\s_-]*\d+)*(?:[\s_-].*)?$/i;
/** Inkscape group ids ("g123", "g12-5"). Case-sensitive so hall-G booths like "G7" stay booth numbers. */
const INKSCAPE_GROUP_ID_RE = /^g\d+(?:[_-]\d+)*$/;
/** Illustrator's duplicate-id suffix ("D3_x2E_5_1_" -> "D3.5" + "_1_"). */
const ILLUSTRATOR_DUPLICATE_SUFFIX_RE = /_\d+_$/;

function isGenericId(value) {
  return GENERIC_ID_RE.test(value) || INKSCAPE_GROUP_ID_RE.test(value);
}

/**
 * Heuristic of the legacy SVG area import only (group names vs. booth numbers): booth-like names contain a
 * digit ("A1", "D3.5", "H2-14") and are short; group names like "Booths" are not. Not a validity check of
 * booth numbers (see checkBoothNumber).
 */
function looksLikeBoothNumber(value) {
  return typeof value === 'string' && value.length > 0 && value.length <= 24 && /\d/.test(value);
}

function tagOf(node) {
  const name = node && (node.localName || node.tagName || node.nodeName);
  return typeof name === 'string' ? name.replace(/^.*:/, '').toLowerCase() : '';
}

function attr(node, name) {
  if (!node || typeof node.getAttribute !== 'function') return null;
  const value = node.getAttribute(name);
  return value === null || value === undefined ? null : String(value);
}

function childElements(node) {
  if (!node) return [];
  const list = node.children || node.childNodes || [];
  return Array.from(list).filter((child) => child && (child.nodeType === undefined || child.nodeType === 1));
}

/**
 * Decodes Illustrator's escaped ids ("D3_x2E_5" -> "D3.5"), strips "booth-"/"stand-" prefixes and
 * Illustrator's duplicate suffix ("D3.5_1_" -> "D3.5") when the rest is a plausible booth number.
 * A "-<n>" suffix is kept: it cannot be told apart from hall-stand numbers like "H2-14".
 */
function boothNumberFromId(id) {
  if (!id) return '';
  const decoded = id.replace(/_x([0-9A-Fa-f]{2,4})_/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  if (isGenericId(decoded)) return '';
  let number = cleanBoothNumber(decoded.replace(/^(?:booth|stand)[\s_-]+/i, ''));
  const withoutSuffix = cleanBoothNumber(number.replace(ILLUSTRATOR_DUPLICATE_SUFFIX_RE, ''));
  if (withoutSuffix !== number && looksLikeBoothNumber(withoutSuffix)) number = withoutSuffix;
  return isGenericId(number) ? '' : number;
}

function titleOf(node) {
  const title = childElements(node).find((child) => tagOf(child) === 'title');
  return title ? cleanBoothNumber(title.textContent) : '';
}

/** Booth number of an element: data-booth-number, data-booth, id, direct <title> child. */
function ownBoothNumber(node) {
  return (
    cleanBoothNumber(attr(node, 'data-booth-number')) ||
    cleanBoothNumber(attr(node, 'data-booth')) ||
    boothNumberFromId(attr(node, 'id')) ||
    titleOf(node)
  );
}

const SHAPE_TAGS = new Set(['polygon', 'rect', 'path']);
const GROUP_TAGS = new Set(['g', 'a', 'svg']);

/** True if a shape or group below `node` carries its own booth number. */
function hasNumberedDescendant(node) {
  return childElements(node).some((child) => {
    const tag = tagOf(child);
    if (SKIPPED_CONTAINERS.has(tag)) return false;
    if (SHAPE_TAGS.has(tag)) return Boolean(ownBoothNumber(child));
    if (GROUP_TAGS.has(tag)) return Boolean(ownBoothNumber(child)) || hasNumberedDescendant(child);
    return false;
  });
}

function rectPairs(node) {
  const x = Number(attr(node, 'x') || 0);
  const y = Number(attr(node, 'y') || 0);
  const w = Number(attr(node, 'width'));
  const h = Number(attr(node, 'height'));
  if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
  return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
}

/** Outline of a path made of M/L/H/V/Z commands (absolute or relative); first subpath only. Null if unsupported. */
function pathPairs(d) {
  if (typeof d !== 'string' || !d.trim()) return null;
  const tokens = d.match(/[A-Za-z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g) || [];
  // Anything that is neither a token nor a separator makes the path unparsable
  const leftover = d.replace(/[A-Za-z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g, '').replace(/[\s,]+/g, '');
  if (leftover) return null;

  const pairs = [];
  let cmd = null;
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;
  let i = 0;
  let subpaths = 0;
  const num = () => {
    const value = Number(tokens[i]);
    i += 1;
    return value;
  };
  const isNumberToken = (token) => token !== undefined && !/^[A-Za-z]$/.test(token);

  while (i < tokens.length) {
    if (/^[A-Za-z]$/.test(tokens[i])) {
      cmd = tokens[i];
      i += 1;
      if (!'MmLlHhVvZz'.includes(cmd)) return null;
      if (cmd === 'Z' || cmd === 'z') {
        x = startX;
        y = startY;
        // Only the first closed subpath is the booth outline
        if (pairs.length >= 3) break;
        continue;
      }
    } else if (cmd === null || cmd === 'Z' || cmd === 'z') {
      return null;
    }
    const relative = cmd === cmd.toLowerCase();
    switch (cmd.toUpperCase()) {
      case 'M': {
        if (!isNumberToken(tokens[i]) || !isNumberToken(tokens[i + 1])) return null;
        const nx = num();
        const ny = num();
        subpaths += 1;
        if (subpaths > 1 && pairs.length >= 3) return pairs;
        x = relative ? x + nx : nx;
        y = relative ? y + ny : ny;
        startX = x;
        startY = y;
        pairs.length = 0;
        pairs.push([x, y]);
        // Further coordinate pairs after a moveto are implicit linetos
        cmd = relative ? 'l' : 'L';
        break;
      }
      case 'L': {
        if (!isNumberToken(tokens[i]) || !isNumberToken(tokens[i + 1])) return null;
        const nx = num();
        const ny = num();
        x = relative ? x + nx : nx;
        y = relative ? y + ny : ny;
        pairs.push([x, y]);
        break;
      }
      case 'H': {
        if (!isNumberToken(tokens[i])) return null;
        const nx = num();
        x = relative ? x + nx : nx;
        pairs.push([x, y]);
        break;
      }
      case 'V': {
        if (!isNumberToken(tokens[i])) return null;
        const ny = num();
        y = relative ? y + ny : ny;
        pairs.push([x, y]);
        break;
      }
      default:
        return null;
    }
    if (pairs.some(([px, py]) => !Number.isFinite(px) || !Number.isFinite(py))) return null;
  }
  return pairs.length ? pairs : null;
}

function svgSize(root) {
  const viewBox = attr(root, 'viewBox');
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
      return { width: parts[2], height: parts[3] };
    }
  }
  const width = positiveNumberOrNull(parseFloat(attr(root, 'width')));
  const height = positiveNumberOrNull(parseFloat(attr(root, 'height')));
  return { width, height };
}

function createParser(DOMParserImpl) {
  const impl = DOMParserImpl || (typeof DOMParser !== 'undefined' ? DOMParser : undefined);
  if (!impl) return null;
  if (typeof impl === 'function') return new impl();
  if (typeof impl.parseFromString === 'function') return impl;
  return null;
}

/**
 * Reads booth outlines from an SVG hall plan: <polygon>, <rect> and <path> (M/L/H/V/Z only)
 * whose booth number comes from data-booth-number, data-booth, id or a direct <title> child;
 * shapes inside a <g> with a booth number inherit it. A group name that does not look like a
 * booth number (no digit, e.g. "Booths") is only inherited when nothing inside the group has its
 * own number. Areas named after such a group, or after a group that also holds numbered shapes
 * (e.g. a hall outline in "Halle_3"), are reported as warnings. Groups themselves never become
 * areas, and shapes without a number are skipped.
 * Transforms are not applied - export the plan with flattened transforms.
 *
 * @param {string} svgText
 * @param {Function|object} [DOMParserImpl] DOMParser constructor (default: global DOMParser) or a parser instance
 * @returns {{width: number|null, height: number|null, areas: object[], errors: string[]}}
 */
export function parseAreasFromSvgText(svgText, DOMParserImpl) {
  const errors = [];
  const empty = (message) => ({ width: null, height: null, areas: [], errors: [message] });
  if (typeof svgText !== 'string' || !svgText.trim()) return empty('The SVG file is empty');
  const parser = createParser(DOMParserImpl);
  if (!parser) return empty('SVG parsing is not available in this environment');

  let doc;
  try {
    doc = parser.parseFromString(svgText, 'image/svg+xml');
  } catch (error) {
    return empty(`The SVG file could not be read: ${error.message}`);
  }
  const root = doc && (doc.documentElement || (typeof doc.querySelector === 'function' ? doc.querySelector('svg') : null));
  if (!root || tagOf(root) !== 'svg') {
    const parserError = doc && typeof doc.getElementsByTagName === 'function' ? doc.getElementsByTagName('parsererror')[0] : null;
    return empty(parserError ? `The SVG file could not be read: ${cleanBoothNumber(parserError.textContent)}` : 'The file is not an SVG document');
  }

  const { width, height } = svgSize(root);
  const areas = [];

  // inherited: { number, suspicious, warned } of the closest numbered group, or null
  const visit = (node, inherited) => {
    for (const child of childElements(node)) {
      const tag = tagOf(child);
      if (SKIPPED_CONTAINERS.has(tag)) continue;
      if (GROUP_TAGS.has(tag)) {
        const own = ownBoothNumber(child);
        let next = inherited;
        if (own) {
          const plausible = looksLikeBoothNumber(own);
          const hasNumbered = hasNumberedDescendant(child);
          // A non-booth-like group name ("Booths") around numbered booths is a container, not a booth
          if (plausible || !hasNumbered) next = { number: own, suspicious: !plausible || hasNumbered, warned: false };
        }
        visit(child, next);
        continue;
      }
      if (!SHAPE_TAGS.has(tag)) continue;
      const ownNumber = ownBoothNumber(child);
      const boothNumber = ownNumber || (inherited ? inherited.number : '');
      if (!boothNumber) continue;
      if (!ownNumber && inherited.suspicious && !inherited.warned) {
        inherited.warned = true;
        errors.push(`"${boothNumber}": booth number taken from a group name - check that it is a booth number`);
      }
      let pairs = null;
      if (tag === 'rect') pairs = rectPairs(child);
      else if (tag === 'polygon') pairs = parsePoints(attr(child, 'points'));
      else pairs = pathPairs(attr(child, 'd'));
      if (!pairs || pairs.length === 0) {
        errors.push(`"${boothNumber}": the <${tag}> outline could not be read (only straight-line shapes are supported)`);
        continue;
      }
      const extra = {
        area: attr(child, 'data-area'),
        width: attr(child, 'data-width'),
        depth: attr(child, 'data-depth'),
      };
      const area = buildArea(boothNumber, pairs, extra, `"${boothNumber}"`);
      if (typeof area === 'string') errors.push(area);
      else areas.push(area);
    }
  };
  visit(root, null);

  return { width, height, areas: dedupeAreas(areas, errors), errors };
}

/**
 * Export file of the plan areas; parseAreasFromJson reads it back. variant / size / positionType / zone / openSides are written when
 * set, bookable only when false (null/true = bookable).
 */
export function areasToExportJson({ width = null, height = null, areas = [] } = {}) {
  const out = (Array.isArray(areas) ? areas : []).map((area) => {
    const entry = {
      boothNumber: area.boothNumber,
      points: area.points,
      labelX: finiteOrNull(area.labelX),
      labelY: finiteOrNull(area.labelY),
    };
    for (const key of ['area', 'width', 'depth']) {
      if (area[key] !== null && area[key] !== undefined) entry[key] = area[key];
    }
    const variant = area.variant === null || area.variant === undefined ? '' : String(area.variant).trim();
    if (variant) entry.variant = variant;
    if (positiveNumberOrNull(area.size) !== null) entry.size = Number(area.size);
    if (area.bookable === false) entry.bookable = false;
    Object.assign(entry, featureFields(area));
    return entry;
  });
  return JSON.stringify({ width: finiteOrNull(width), height: finiteOrNull(height), areas: out }, null, 2);
}

// ---------------------------------------------------------------------------
// Checkout and holds
// ---------------------------------------------------------------------------

/**
 * Checkout link: one productId param per booth (compat with the existing checkout flow) plus one
 * `booth=<productId>:<boothNumber>` param per item with a booth number (split at the first colon by
 * buildCheckoutProducts). `items` ([{ productId, boothNumber }]) come first, then the legacy
 * `productIds` (no booth param). `notes` is informational order data.
 */
export function buildCheckoutUrl({ items = [], productIds = [], companyId, eventId, notes } = {}) {
  const params = new URLSearchParams();
  const itemList = (Array.isArray(items) ? items : [items]).filter((item) => item && item.productId);
  for (const item of itemList) params.append('productId', item.productId);
  for (const id of Array.isArray(productIds) ? productIds : [productIds]) {
    if (id) params.append('productId', id);
  }
  for (const item of itemList) {
    const number = cleanBoothNumber(item.boothNumber);
    if (number) params.append('booth', `${item.productId}:${number}`);
  }
  if (companyId) params.set('companyId', companyId);
  if (eventId) params.set('eventId', eventId);
  if (notes) params.set('notes', notes);
  return `/checkout?${params.toString()}`;
}

/** Query-string value (string | string[] | undefined, e.g. from Checkout's extractExistingParams) as a list of non-empty strings. */
function paramList(value) {
  const list = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return list.filter((v) => typeof v === 'string' && v.trim() !== '').map((v) => v.trim());
}

/** 'p1:N3.1' -> { productId: 'p1', boothNumber: 'N3.1' } (split at the FIRST colon), null when a part is empty. */
function parseBoothParam(value) {
  const index = value.indexOf(':');
  if (index <= 0) return null;
  const productId = value.slice(0, index).trim();
  const boothNumber = cleanBoothNumber(value.slice(index + 1));
  return productId && boothNumber ? { productId, boothNumber } : null;
}

/**
 * Checkout lines from the query params (pure; Checkout.jsx uses it after fetching the products).
 *  - params.productId / params.booth: string or string[];
 *  - every productId occurrence consumes the next booth param of that product (`<productId>:<number>`,
 *    first colon) -> one entry per booth with qty 1 and lineBoothNumber (NOT boothNumber, which is
 *    Product.boothNumber); a repeated (productId, normalized number) pair counts once and extra
 *    occurrences of a product that has booth params are dropped;
 *  - products WITHOUT booth params keep today's behaviour: one entry with qty = occurrences and
 *    lineBoothNumber null;
 *  - booth params without a productId occurrence and productIds missing from productsById are ignored.
 * Entries are shallow copies of the product, in order of the productId params.
 *
 * @param {{productId?: string|string[], booth?: string|string[]}} params
 * @param {Map<string, object>|Object<string, object>} productsById
 * @returns {object[]} [{ ...product, qty, lineBoothNumber }]
 */
export function buildCheckoutProducts(params, productsById) {
  const lookup = (id) => {
    if (!productsById) return undefined;
    return productsById instanceof Map ? productsById.get(id) : productsById[id];
  };
  const productIds = paramList(params && params.productId);
  const boothsByProduct = new Map();
  for (const raw of paramList(params && params.booth)) {
    const booth = parseBoothParam(raw);
    if (!booth) continue;
    if (!boothsByProduct.has(booth.productId)) boothsByProduct.set(booth.productId, { list: [], keys: new Set() });
    const entry = boothsByProduct.get(booth.productId);
    const key = normalizeBoothNumber(booth.boothNumber);
    if (entry.keys.has(key)) continue;
    entry.keys.add(key);
    entry.list.push(booth.boothNumber);
  }

  const out = [];
  const fallback = new Map();
  const consumed = new Map();
  for (const id of productIds) {
    const product = lookup(id);
    if (!product) continue;
    const booths = boothsByProduct.get(id);
    if (booths) {
      const index = consumed.get(id) || 0;
      if (index < booths.list.length) out.push({ ...product, qty: 1, lineBoothNumber: booths.list[index] });
      consumed.set(id, index + 1);
      continue;
    }
    const existing = fallback.get(id);
    if (existing) {
      existing.qty += 1;
    } else {
      const entry = { ...product, qty: 1, lineBoothNumber: null };
      fallback.set(id, entry);
      out.push(entry);
    }
  }
  return out;
}

/** Whole seconds left on a hold (0 when expired or unknown). */
export function holdRemainingSeconds(expiresAtIso, nowMs = Date.now()) {
  if (!expiresAtIso) return 0;
  const end = new Date(expiresAtIso).getTime();
  if (!Number.isFinite(end) || !Number.isFinite(nowMs)) return 0;
  return Math.max(0, Math.ceil((end - nowMs) / 1000));
}

/** 754 -> "12:34". */
export function formatCountdown(seconds) {
  const total = Number.isFinite(Number(seconds)) ? Math.max(0, Math.floor(Number(seconds))) : 0;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Asset helpers
// ---------------------------------------------------------------------------

/** Storage-safe file name: ASCII letters, digits, ".", "_", "-"; lowercase extension. */
export function sanitizeAssetFileName(name, maxLength = 120) {
  const raw = typeof name === 'string' ? name.split(/[\\/]/).pop().trim() : '';
  const ascii = raw
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '');
  const dot = ascii.lastIndexOf('.');
  const hasExt = dot > 0 && dot < ascii.length - 1;
  const ext = hasExt ? ascii.slice(dot + 1).replace(/[^A-Za-z0-9]/g, '').toLowerCase() : '';
  const suffix = ext ? `.${ext}` : '';
  let base = (hasExt ? ascii.slice(0, dot) : ascii)
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  const room = Math.max(1, maxLength - suffix.length);
  base = base.slice(0, room).replace(/[-.]+$/g, '');
  return `${base || 'file'}${suffix}`;
}

const CONTENT_TYPES = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  glb: 'model/gltf-binary',
  json: 'application/json',
};

/** Content type to send with uploadData (S3 would store application/octet-stream otherwise). */
export function contentTypeForFileName(name) {
  const match = typeof name === 'string' ? /\.([A-Za-z0-9]+)$/.exec(name.trim()) : null;
  return (match && CONTENT_TYPES[match[1].toLowerCase()]) || 'application/octet-stream';
}

/** "public/a/b.svg" -> "a/b.svg" (Amplify Storage key for accessLevel guest). */
export function stripPublicPrefix(s3Path) {
  return typeof s3Path === 'string' ? s3Path.replace(/^public\//, '') : '';
}
